import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';

// material-ui
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

// project imports
import Avatar from 'ui-component/extended/Avatar';

// assets
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupIcon from '@mui/icons-material/Group';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SellIcon from '@mui/icons-material/Sell';
import TranslateIcon from '@mui/icons-material/Translate';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import HttpsIcon from '@mui/icons-material/Https';
import CircularProgress from '@mui/material/CircularProgress';

import SkeletonTableRow from '../InfluencerList/SkeletonTableRow';
import SocialMediaIcon from '../Extra/SocialMediaIcon';
import { BC_COUNTRIES } from 'constants/bcCountries';

import { updateSelectedInfluencers, showPremiumFeatureDialog, updateUser } from 'actions/planner2';
import { checkProfilePermission, unlockProfile } from 'api_handlers/planner2';
import { fetchNewInfluencer } from 'api_handlers/planner2';
import { getDiscoveryInfluencersDetails } from 'api_handlers/planner2';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions } from '@mui/material';
import Popover from '@mui/material/Popover';

import SearchReasonCard from '../Extra/SearchReasonCard';
import mixpanel from '../../../../utils/mixpanel';
import CreatorTrustModal from './CreatorTrustModal';
import { getCreatorTrustBatch } from 'api_handlers/discovery';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';

import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';

// Trust tiers -> chip colors (same palette as the Creator Trust popup).
const TRUST_TIER = {
  trusted: { color: '#0f7a3d', bg: '#e7f6ed' },
  reliable: { color: '#1c5fd6', bg: '#e8f0fe' },
  needs_review: { color: '#8a6d1a', bg: '#fff6df' },
  high_risk: { color: '#b3261e', bg: '#fdecea' },
};

// Controversy / politics / protest flag shown next to the trust score.
// Only the levels that carry a real signal get an icon (clean/unknown: none).
const SENSITIVITY_FLAG = {
  low: { color: '#8a90a0', label: 'Some sensitive content' },
  elevated: { color: '#c88a1f', label: 'Sensitive content' },
  high: { color: '#b3261e', label: 'Heavy sensitive content' },
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

// --- category colour + emoji (same look as the search-bar suggestions) ---
const CAT_PALETTE = [
  { bg: '#FFF1E6', fg: '#C2410C' },
  { bg: '#E8F2FF', fg: '#1D4ED8' },
  { bg: '#E9F8EE', fg: '#15803D' },
  { bg: '#F4ECFF', fg: '#7C3AED' },
  { bg: '#FFEAF1', fg: '#BE185D' },
  { bg: '#FFF7DB', fg: '#A16207' },
  { bg: '#E6FAF6', fg: '#0F766E' },
  { bg: '#EEF1F8', fg: '#374151' },
];
const catStyle = (name = '') => {
  const str = typeof name === 'string' ? name : '';
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return CAT_PALETTE[h % CAT_PALETTE.length];
};
const CAT_EMOJI = {
  fashion: '👗', 'fashion male': '🧥', 'sustainable fashion': '🌿', beauty: '💄', skincare: '🧴',
  haircare: '💇', travel: '✈️', photography: '📷', entertainment: '🎬', 'entertainment page': '🎬',
  health: '🩺', gaming: '🎮', actor: '🎭', education: '🎓', dance: '💃', parenting: '👶',
  'digital marketing': '📈', 'meme page': '😂', 'fan page': '⭐', 'stock market': '📊',
  infotainment: '📰', 'graphic designer': '🎨', automobile: '🚗', finance: '💰', yoga: '🧘',
  'daily vlogger': '🎥', theatre: '🎭', spirituality: '🕉️', 'career coach': '💼', literature: '📚',
  legal: '⚖️', agriculture: '🌾', gardening: '🌱', sustainability: '♻️', 'gadget review': '📱',
  'news and media': '📰', motivational: '🔥', motivation: '🔥', 'self help and motivation': '🌟',
  'art and craft': '🎨', 'pet page': '🐾', 'website and tools': '🛠️', aviation: '🛩️', food: '🍔',
  fitness: '💪', music: '🎵', technology: '💻', lifestyle: '✨', comedy: '😂', sports: '🏏', other: '🏷️',
};
const catEmoji = (name = '') => CAT_EMOJI[(typeof name === 'string' ? name : '').toLowerCase()] || '🏷️';

const CategoryChip = ({ label }) => {
  const cs = catStyle(label);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: cs.bg,
        color: cs.fg,
        fontWeight: 600,
        fontSize: 11,
        padding: '4px 9px',
        borderRadius: 8,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: 11 }}>{catEmoji(label)}</span>
      {label}
    </span>
  );
};

const getCategoryChips = (row, bcRosterOnly, aiMode = false) => {
  const categories = [];

  const rawBcTopics =
    row?.topic_categories || row?.topicCategories || row?.bc_public_data?.topicCategories || [];

  if (bcRosterOnly === 'yes' && Array.isArray(rawBcTopics) && rawBcTopics.length > 0) {
    rawBcTopics.forEach((urlOrLabel) => {
      if (!urlOrLabel) return;
      if (typeof urlOrLabel === 'string') {
        const match = urlOrLabel.match(/\/wiki\/(.+)$/);
        const label = match ? decodeURIComponent(match[1]).replace(/_/g, ' ') : urlOrLabel;
        if (label) categories.push(label);
      }
    });
  } else {
    if (aiMode && row?.primary_content_category) {
      categories.push(row.primary_content_category);
    }
    const primaryCategoryName = row?.primary_category?.name;
    if (primaryCategoryName && primaryCategoryName !== 'Other') {
      categories.push(primaryCategoryName);
    } else if (!aiMode && row?.primary_content_category) {
      categories.push(row.primary_content_category);
    }
    if (Array.isArray(row?.interest_categories)) {
      row.interest_categories.forEach((category) => {
        if (typeof category === 'string') categories.push(category);
      });
    }
  }

  const unique = Array.from(new Set(categories)).filter(Boolean);
  const categoryChips = unique.slice(0, 2).map((category, index) => (
    <Grid item key={index}>
      <CategoryChip label={category} />
    </Grid>
  ));

  if (unique.length <= 2) return categoryChips;

  return (
    <>
      {categoryChips}
      <Grid item>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: '#FFF7DB',
            color: '#A16207',
            fontWeight: 600,
            fontSize: 11,
            padding: '4px 9px',
            borderRadius: 8,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          +{unique.length - 2}
        </span>
      </Grid>
    </>
  );
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fcfaf2',
    '& .action-container': {
      visibility: 'visible',
      opacity: 1,
    },
  },
}));

const HeaderCell = ({ name, Icon, align = 'center' }) => (
  <TableCell sx={{ borderTop: 'none', py: 1.5 }} align={align}>
    <Stack direction="row" alignItems="center" justifyContent={align} spacing={0.75}>
      {Icon ? <Icon sx={{ fontSize: 16, color: '#9aa1b2' }} /> : null}
      <Typography
        className="dv2-th"
        sx={{ fontSize: '11.5px', fontWeight: 700, letterSpacing: '0.4px', color: '#5b6478' }}
      >
        {name}
      </Typography>
    </Stack>
  </TableCell>
);

const MetricCell = ({ children }) => (
  <TableCell align="center" sx={{ py: 1.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1f2433' }}>
      {children == null || children === '' ? '—' : children}
    </Typography>
  </TableCell>
);

// Initials fallback shown when a profile image fails to load (e.g. expired
// Instagram CDN signatures). MUI Avatar renders children when `src` errors.
const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || '';
  const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (a + b).toUpperCase() || '?';
};

// Pull a readable @handle out of the profile url.
const getHandle = (row) => {
  if (!row?.url) return '';
  try {
    const parts = String(row.url).split('?')[0].split('/').filter(Boolean);
    const last = parts[parts.length - 1];
    if (!last || last.includes('.com')) return '';
    return '@' + last.replace(/^@/, '');
  } catch (e) {
    return '';
  }
};

// Render follower/subscriber counts without breaking pre-formatted values.
// The API may return a plain number (123456), a formatted string ("123.4K"),
// or null — handle all three gracefully.
const formatCount = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' && /[a-zA-Z]/.test(value)) return value; // already "12.3K"
  const num = Number(String(value).replace(/,/g, ''));
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('en-IN');
};

const relevancyChipColor = (score) => {
  const v = parseFloat(score);
  if (Number.isNaN(v)) return { bg: '#eef0f5', fg: '#5b6478', border: '#d1d5db' };
  if (v >= 7) return { bg: '#e6f7ed', fg: '#1b7a45', border: '#86efac' };  // green
  if (v >= 4) return { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' };  // blue
  return { bg: '#fff4e0', fg: '#9a6a12', border: '#fcd34d' };              // orange
};

// Colour the engagement-rate chip by quality.
const erChipColor = (er) => {
  const value = parseFloat(String(er).replace('%', ''));
  if (Number.isNaN(value)) return { bg: '#eef0f5', fg: '#5b6478' };
  if (value >= 3) return { bg: '#e6f7ed', fg: '#1b7a45' };
  if (value >= 1) return { bg: '#fff4e0', fg: '#9a6a12' };
  return { bg: '#fdecec', fg: '#b23b3b' };
};

const UserListV2 = ({ influencers, loading, handleAddToCampaign, aiMode = false }) => {
  const user = useSelector((state) => state.planner2.user);
  const selected = useSelector((state) => state.planner2.selectedInfluencers);
  const autoCompleteValues = useSelector((state) => state.planner2.autoCompleteValues || []);
  const bcRosterOnly = useSelector((state) => state.plannerFilters?.bcRosterOnly || 'no');

  const dispatch = useDispatch();

  const [profilePermissionMap, setProfilePermissionMap] = React.useState({});
  const [unlockedProfilePermissionMap, setUnlockedProfilePermissionMap] = React.useState({});
  const [loaderMap, setLoaderMap] = React.useState({});

  const [showUnlockedProfileCreditOver, setShowUnlockedProfileCreditOver] = React.useState(false);
  const [showPremiumProfileCreditOver, setShowPremiumProfileCreditOver] = React.useState(false);

  const [searchReasonAnchor, setSearchReasonAnchor] = React.useState(null);
  const [activeSearchReason, setActiveSearchReason] = React.useState('');
  const [showIQProfileNotExists, setShowIQProfileNotExists] = React.useState(false);

  const [vibrate, setVibrate] = useState(false);

  // Trust scores for the visible page (one batch call), + which creator's
  // Trust popup is open.
  const [trustMap, setTrustMap] = useState({});
  const [trustOpen, setTrustOpen] = useState(false);
  const [trustId, setTrustId] = useState(null);

  useEffect(() => {
    const ids = (influencers || []).map((inf) => inf.id).filter(Boolean);
    if (!ids.length) return;
    let alive = true;
    getCreatorTrustBatch(ids)
      .then((res) => {
        if (alive && res && res.results) setTrustMap((prev) => ({ ...prev, ...res.results }));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [influencers]);

  const openTrust = (id) => {
    setTrustId(id);
    setTrustOpen(true);
  };

  const skeletonCount = Array.from({ length: 20 });

  const fullNameById = React.useMemo(() => {
    const map = {};
    for (const opt of autoCompleteValues) {
      if (!opt || opt.group !== 'Influencers') continue;
      if (opt.type !== 'influencers-found') continue;
      if (!opt.id) continue;
      map[opt.id] = opt.display_name || opt.displayName || opt.name;
    }
    return map;
  }, [autoCompleteValues]);

  const getLocationDisplay = (row) => {
    if (bcRosterOnly === 'yes') {
      const raw = row?.audience_country_distribution_max ?? row?.audienceCountryDistributionMax;
      const val = Array.isArray(raw) ? raw[0] : raw;
      const code = typeof val === 'string' ? val.trim().toUpperCase() : '';
      return BC_COUNTRIES?.[code] || val || row?.location || 'NA';
    }
    return row?.location || 'Other';
  };

  const getNameDisplay = (row) => fullNameById?.[row?.id] || row?.name || '';

  const setSelected = (newSelected) => {
    dispatch(updateSelectedInfluencers(newSelected));
  };

  const onSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = influencers.map((n) => n.id);
      setSelected(new Set(newSelecteds));
      if (process.env.NODE_ENV === 'production') {
        mixpanel.track('discover_select_all_list');
      }
      return;
    }
    setSelected(new Set());
  };

  const handleClick = (event, id) => {
    if (selected.has(id)) {
      const newSelected = new Set(selected);
      newSelected.delete(id);
      setSelected(newSelected);
    } else {
      setSelected(new Set(selected).add(id));
    }
  };

  const fetchDetails = async (getId) => {
    if (getId) {
      setLoaderMap((prevLoaderMap) => {
        const newLoaderMap = { ...prevLoaderMap, [getId]: !vibrate ? false : true };
        return newLoaderMap;
      });

      try {
        const response = await getDiscoveryInfluencersDetails(getId);
        return response;
      } catch (error) {
        console.error('Error fetching details:', error);
        setLoaderMap((prevLoaderMap) => {
          const newLoaderMap = { ...prevLoaderMap, [getId]: false };
          return newLoaderMap;
        });
      } finally {
        setLoaderMap((prevLoaderMap) => {
          const newLoaderMap = { ...prevLoaderMap, [getId]: false };
          return newLoaderMap;
        });
      }
    }
  };

  const clearLoaderForRow = (rowId) => {
    setLoaderMap((prev) => ({ ...prev, [rowId]: false }));
  };

  const handleShowDetails = async (row, open) => {
    setLoaderMap((prev) => ({ ...prev, [row.id]: true }));
    try {
      let response = await fetchDetails(row.id);
      if (response && response.url && !row.url) {
        row.url = response.url;
      }
      if (response?.is_brandconnect === 'yes') {
        openProfileHandler(row, false);
        return;
      }
      if (profilePermissionMap[row.id]) {
        if (!unlockedProfilePermissionMap[row.id]) {
          try {
            const resp = await unlockProfile(row.id, false);
            if (resp?.detail === 'Success') {
              openProfileHandler(row, false);
            } else {
              clearLoaderForRow(row.id);
            }
          } catch (error) {
            clearLoaderForRow(row.id);
            if (error && error.response && error.response.status === 403) {
              showUnlockedProfileCreditOver(true);
            }
          }
        } else {
          openProfileHandler(row, false);
        }
      } else {
        if (open === true) {
          setVibrate((v) => !v);
          setTimeout(() => setVibrate(false), 1000);
          clearLoaderForRow(row.id);
        } else {
          unlockProfileHandler(row);
        }
      }
    } catch (err) {
      clearLoaderForRow(row.id);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(async () => {
    const resp = await checkProfilePermission({
      payload: {
        profile_ids: influencers.map((inf) => inf.id),
      },
    });
    setProfilePermissionMap(resp['profile_id_access_map']['standard-profiles'] || {});
    setUnlockedProfilePermissionMap(resp['profile_id_access_map']['unlocked-profiles'] || {});
  }, [influencers]);

  const openProfileHandler = (row, is_premium) => {
    dispatch(updateUser());
    setProfilePermissionMap((prevData) => ({
      ...prevData,
      [row.id]: true,
    }));
    setUnlockedProfilePermissionMap((prevData) => ({
      ...prevData,
      [row.id]: true,
    }));
    setLoaderMap((prevLoaderMap) => {
      const newLoaderMap = { ...prevLoaderMap, [row.id]: false };
      return newLoaderMap;
    });
    // 'Insights viewed' is tracked by the profile page itself; type/source ride along in the query.
    window.open(
      `/planner/profile-details/${row.platform}/${
        row.platform === 'instagram' ? `${row.url.split('/')[3]}` : `${row.url.split('/')[4]}`
      }?type=${is_premium ? 'Premium' : 'standard'}&src=discover`,
      '_blank',
    );
  };

  const unlockProfileHandler = async (row) => {
    setLoaderMap((prevLoaderMap) => {
      const newLoaderMap = { ...prevLoaderMap, [row.id]: true };
      return newLoaderMap;
    });
    try {
      if (!row.audience_data_exist) {
        const { data } = await fetchNewInfluencer({
          payload: {
            link: row.url,
            'with-audience-data': true,
          },
        }, 'Discover › Unlock profile');
        if (data.data && data.data.dump && Object.keys(data.data.dump).length === 0) {
          setShowIQProfileNotExists(true);
          return;
        }
      }

      const resp = await unlockProfile(row.id, true);
      if (resp?.detail === 'Success') {
        openProfileHandler(row, true);
      } else {
        setLoaderMap((prevLoaderMap) => {
          const newLoaderMap = { ...prevLoaderMap, [row.id]: false };
          return newLoaderMap;
        });
      }
    } catch (error) {
      setLoaderMap((prevLoaderMap) => {
        const newLoaderMap = { ...prevLoaderMap, [row.id]: false };
        return newLoaderMap;
      });
      if (error && error.response && error.response.status === 403) {
        setShowPremiumProfileCreditOver(true);
      }
    }
  };

  return (
    <TableContainer className="dv2-table-wrap" sx={{ borderRadius: '12px' }}>
      <Table sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #f0f1f5' } }}>
        <TableHead>
          <TableRow sx={{ background: '#fbfbfd' }}>
            <TableCell sx={{ borderTop: 'none', pl: 2 }} padding="checkbox">
              <Checkbox
                color="primary"
                indeterminate={selected.size > 0 && selected.size < influencers.length}
                checked={influencers.length > 0 && selected.size === influencers.length}
                onChange={onSelectAllClick}
                inputProps={{ 'aria-label': 'select all influencers' }}
              />
            </TableCell>
            <HeaderCell name="#" />
            <HeaderCell name="Creator" Icon={AccountCircleIcon} align="left" />
            {aiMode && <HeaderCell name="Relevance" Icon={AutoAwesomeIcon} />}
            <HeaderCell name="Followers" Icon={GroupIcon} />
            <HeaderCell name="ER" Icon={GroupAddIcon} />
            <HeaderCell name="Trust" Icon={VerifiedUserOutlinedIcon} />
            <HeaderCell name="Avg Views" Icon={RemoveRedEyeIcon} />
            <HeaderCell name="Avg Engagement" Icon={FavoriteBorderIcon} />
            <HeaderCell name="Language" Icon={TranslateIcon} />
            <HeaderCell name="Location" Icon={LocationOnIcon} />
            <HeaderCell name="Categories" Icon={SellIcon} />
            <TableCell sx={{ maxWidth: 0, borderTop: 'none' }} align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? skeletonCount.map((_, index) => <SkeletonTableRow key={index} />) : null}

          {influencers && influencers.length > 0
            ? influencers.map((row, index) => {
                const isItemSelected = selected.has(row.id);
                const handle = getHandle(row);
                const er = row.engagement_rate;
                const erColors = erChipColor(er);
                return (
                  <StyledTableRow hover key={row.id} style={{ position: 'relative' }}>
                    <TableCell
                      sx={{ pl: 2 }}
                      padding="checkbox"
                      onClick={(event) => handleClick(event, row.id)}
                    >
                      <Checkbox color="primary" checked={isItemSelected} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography className="dv2-rank" sx={{ fontSize: 12, fontWeight: 700, color: '#9aa1b2' }}>
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: 230, maxWidth: 230 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                        <Avatar
                          alt={getNameDisplay(row)}
                          src={row.picture || undefined}
                          style={{ cursor: 'pointer' }}
                          sx={{
                            bgcolor: '#111936',
                            color: '#febd1c',
                            fontWeight: 700,
                            fontSize: 13.5,
                            flexShrink: 0,
                          }}
                          onClick={async () => {
                            await handleShowDetails(row, true);
                          }}
                        >
                          {getInitials(getNameDisplay(row))}
                        </Avatar>
                        <Stack spacing={0.2} sx={{ minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              fontSize={'0.85rem'}
                              sx={{
                                cursor: 'pointer',
                                color: '#1f2433',
                                '&:hover': { color: '#a88a1f' },
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.25,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                              onClick={async () => {
                                await handleShowDetails(row, true);
                              }}
                            >
                              {getNameDisplay(row)}
                            </Typography>
                            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              <SocialMediaIcon platform={row.platform} url={row.url} />
                            </Box>
                          </Stack>
                          {handle ? (
                            <Typography className="dv2-handle" sx={{ fontSize: '11.5px', color: '#6b7280' }} noWrap>
                              {handle}
                            </Typography>
                          ) : null}
                        </Stack>
                      </Stack>
                    </TableCell>
                    {aiMode && (
                      <TableCell align="center">
                        {row.relevancy_score != null ? (
                          <Chip
                            size="small"
                            label={Number(row.relevancy_score).toFixed(1)}
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              bgcolor: relevancyChipColor(row.relevancy_score).bg,
                              color: relevancyChipColor(row.relevancy_score).fg,
                              border: `1.5px solid ${relevancyChipColor(row.relevancy_score).border}`,
                              cursor: row.search_reason ? 'pointer' : 'default',
                            }}
                            onClick={row.search_reason ? (e) => {
                              e.stopPropagation();
                              setActiveSearchReason(row.search_reason);
                              setSearchReasonAnchor(e.currentTarget);
                            } : undefined}
                          />
                        ) : (
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1f2433' }}>—</Typography>
                        )}
                      </TableCell>
                    )}
                    <MetricCell>{formatCount(row.followers)}</MetricCell>
                    <TableCell align="center">
                      {er == null || er === '' ? (
                        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }}>—</Typography>
                      ) : (
                        <Chip
                          size="small"
                          label={`${er}${String(er).includes('%') ? '' : '%'}`}
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: erColors.bg,
                            color: erColors.fg,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {(() => {
                        const t = trustMap[String(row.id)];
                        if (!t) {
                          return <CircularProgress size={13} sx={{ color: '#c7ccd8' }} />;
                        }
                        const tier = TRUST_TIER[t.verdict] || TRUST_TIER.needs_review;
                        const flag = SENSITIVITY_FLAG[t.sensitivity_level];
                        const cats = (t.sensitivity_categories || [])
                          .map((c) => c.replace('_', '/'))
                          .join(', ');
                        return (
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                            <Tooltip title={`${t.verdict_label || 'Trust'} — click for the full assessment`}>
                              <Chip
                                size="small"
                                label={t.trust_score == null ? '—' : t.trust_score}
                                onClick={() => openTrust(row.id)}
                                sx={{
                                  color: tier.color,
                                  bgcolor: tier.bg,
                                  fontWeight: 800,
                                  minWidth: 40,
                                  cursor: 'pointer',
                                  '&:hover': { filter: 'brightness(0.97)' },
                                }}
                              />
                            </Tooltip>
                            {flag ? (
                              <Tooltip
                                title={`${flag.label}: ${cats || 'political / protest / controversy'} content — click for details`}
                              >
                                <OutlinedFlagIcon
                                  onClick={() => openTrust(row.id)}
                                  sx={{ fontSize: 17, color: flag.color, cursor: 'pointer' }}
                                />
                              </Tooltip>
                            ) : null}
                          </Stack>
                        );
                      })()}
                    </TableCell>
                    <MetricCell>{row.avg_views}</MetricCell>
                    <MetricCell>{row.prediction?.enagaged_users_display}</MetricCell>
                    <TableCell align="center">
                      <Typography sx={{ fontSize: '0.8rem', color: '#4b5266', textTransform: 'capitalize' }}>
                        {row.language || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontSize: '0.8rem', color: '#4b5266', textTransform: 'capitalize' }}>
                        {getLocationDisplay(row)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Grid container spacing={0.75}>
                        {getCategoryChips(row, bcRosterOnly, aiMode)}
                      </Grid>
                    </TableCell>
                    <TableCell sx={{ maxHeight: '100%', position: 'relative' }} align="right">
                      <Grid
                        container
                        spacing={1}
                        sx={{
                          visibility: 'hidden',
                          opacity: 0,
                          position: 'absolute',
                          right: '1%',
                          top: '-0.1px',
                          marginTop: '1px',
                          width: '600px',
                          paddingRight: '10px',
                          transition: 'opacity 0.25s ease',
                        }}
                        className="action-container"
                        height={'85%'}
                        justifyContent={'flex-end'}
                        alignItems={'center'}
                      >
                        <Grid item>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              if (process.env.NODE_ENV === 'production') {
                                mixpanel.track('discover_add_to_list', { type: 'collab_list' });
                              }
                              handleAddToCampaign([row.id], 'Planner');
                            }}
                            startIcon={
                              <FormatListBulletedRoundedIcon style={{ width: '10px', height: '15px' }} />
                            }
                            sx={{ fontSize: 10, width: '130px' }}
                          >
                            Add to List
                          </Button>
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              if (user.premium_user) handleAddToCampaign([row.id], 'AddToCampaign');
                              else dispatch(showPremiumFeatureDialog(true));
                            }}
                            sx={{ fontSize: 10, width: '130px' }}
                            startIcon={<WorkspacePremiumIcon style={{ width: '10px', height: '15px' }} />}
                          >
                            Add to Campaign
                          </Button>
                        </Grid>
                        <Grid item>
                          <Tooltip
                            title={
                              !profilePermissionMap[row.id]
                                ? 'Unlock this profile for 1 premium credit.'
                                : !user?.premium_user &&
                                  profilePermissionMap[row.id] &&
                                  !unlockedProfilePermissionMap[row.id]
                                ? 'Unlock this profile  for 1 standard credit.'
                                : ''
                            }
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              vibrate={vibrate}
                              onClick={() => {
                                if (user?.credit_balance === 0 && !profilePermissionMap[row.id]) {
                                  setShowPremiumProfileCreditOver(true);
                                } else if (
                                  !user?.premium_user &&
                                  user?.fetched_credit_balance === 0 &&
                                  profilePermissionMap[row.id] &&
                                  !unlockedProfilePermissionMap[row.id]
                                ) {
                                  setShowUnlockedProfileCreditOver(true);
                                } else {
                                  handleShowDetails(row, false);
                                }
                              }}
                              style={{ color: 'black', fontSize: 10, width: '130px' }}
                              sx={{
                                ...(loaderMap[row.id] && { opacity: 0.85, cursor: 'wait' }),
                                animation: vibrate ? 'vibrate 0.2s ease 5' : 'none',
                                '@keyframes vibrate': {
                                  '0%': { transform: 'translateX(0)' },
                                  '25%': { transform: 'translateX(-2px)' },
                                  '50%': { transform: 'translateX(2px)' },
                                  '75%': { transform: 'translateX(-2px)' },
                                  '100%': { transform: 'translateX(0)' },
                                },
                              }}
                              startIcon={
                                loaderMap[row.id] ? (
                                  <CircularProgress size={16} sx={{ color: 'black' }} />
                                ) : profilePermissionMap[row.id] ? (
                                  <RemoveRedEyeIcon style={{ width: '10px', height: '15px' }} />
                                ) : (
                                  <HttpsIcon style={{ width: '10px', height: '15px' }} />
                                )
                              }
                              disabled={loaderMap[row.id]}
                            >
                              {loaderMap[row.id] ? 'Loading...' : 'Show Details'}
                            </Button>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            : null}
        </TableBody>
      </Table>

      {influencers && influencers.length === 0 && !loading ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#6b7280' }}>
            No influencers to show.
          </Typography>
        </Box>
      ) : null}

      <Popover
        open={Boolean(searchReasonAnchor)}
        anchorEl={searchReasonAnchor}
        onClose={() => setSearchReasonAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { maxWidth: 360, p: 1.75, borderRadius: '12px' } }}
      >
        <SearchReasonCard reason={activeSearchReason} />
      </Popover>

      {showUnlockedProfileCreditOver ? (
        <React.Fragment>
          <BootstrapDialog
            onClose={() => {
              setShowUnlockedProfileCreditOver(false);
            }}
            aria-labelledby="customized-dialog-title"
            open={true}
            fullWidth
          >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title"></DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowUnlockedProfileCreditOver(false);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers style={{ textAlign: 'center' }}>
              You’ve reached the limit of unlocking standard profiles. Use your remaining credits to
              unlock premium profiles or upgrade for unlimited access.
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowUnlockedProfileCreditOver(false);
                  dispatch(showPremiumFeatureDialog(true));
                }}
              >
                Upgrade
              </Button>
            </DialogActions>
          </BootstrapDialog>
        </React.Fragment>
      ) : null}
      {showPremiumProfileCreditOver ? (
        <React.Fragment>
          <BootstrapDialog
            onClose={() => {
              setShowPremiumProfileCreditOver(false);
            }}
            aria-labelledby="customized-dialog-title"
            open={true}
            fullWidth
          >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title"></DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowPremiumProfileCreditOver(false);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers style={{ textAlign: 'center' }}>
              You’ve used all your premium credits. Upgrade to unlock more premium profiles!
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowPremiumProfileCreditOver(false);
                  dispatch(showPremiumFeatureDialog(true));
                }}
              >
                Upgrade
              </Button>
            </DialogActions>
          </BootstrapDialog>
        </React.Fragment>
      ) : null}
      {showIQProfileNotExists ? (
        <React.Fragment>
          <BootstrapDialog
            onClose={() => {
              setShowIQProfileNotExists(false);
            }}
            aria-labelledby="customized-dialog-title"
            open={true}
            fullWidth
          >
            <DialogTitle
              sx={{ m: 0, p: 2, textAlign: 'center', fontWeight: 'bold' }}
              id="customized-dialog-title"
            >
              We are fetching the data for this profile.
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => {
                setShowIQProfileNotExists(false);
              }}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={30} sx={{ marginRight: '10px' }} />
                Please wait while we are fetching the latest data for this profile, you can come back
                and check later.
              </div>
            </DialogContent>
          </BootstrapDialog>
        </React.Fragment>
      ) : null}

      <CreatorTrustModal open={trustOpen} setOpen={setTrustOpen} id={trustId} />
    </TableContainer>
  );
};

export default UserListV2;
